import React, { useEffect, useState } from "react"
import { Alert, Button, Checkbox, Input, Modal, Progress, Spin } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch } from "@fortawesome/free-solid-svg-icons"
import { useQueries } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { useUnitUsersQuery } from "hooks/api/useUnitsQuery"
import { queryKeys } from "api/queryKeys"
import { projectMembers } from "api/Repos/projects"
import { useUserStore } from "stores/userStore"
import {
  BulkAction,
  BulkProject,
  BulkResult,
  MembersByProject,
  memberCounts,
  planBulkChanges,
  sameName,
  summariseResults,
} from "./bulkMembers"
import { isRetryable, useBulkMembers } from "./useBulkMembers"
import { limitMembersRead } from "./limiter"

interface BulkMembersModalProps {
  open: boolean
  action: BulkAction
  projects: BulkProject[]
  /**
   * `finished` is true when a run ended, so the caller can clear its ticks;
   * `keepIds` are then the projects that didn't finish (a failed, unreadable
   * or not-run result), to stay ticked. Empty when nothing ran.
   */
  onClose: (finished: boolean, keepIds: string[]) => void
}

const projectsText = (count: number): string =>
  `${count} ${count === 1 ? "project" : "projects"}`

/** Names in first-seen order, repeats (ignoring case) dropped. */
const uniqueNames = (names: string[]): string[] => {
  const seen = new Set<string>()
  return names.filter((name) => {
    const key = sameName(name)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Why a call isn't done, for the list under the summary. */
const reasonOf = (result: BulkResult): string =>
  result.outcome === "not-run" ? "Not run" : result.error || "Failed"

const isProblem = (result: BulkResult): boolean =>
  result.outcome === "failed" ||
  result.outcome === "unreadable" ||
  result.outcome === "not-run"

const byProjectThenPerson = (a: BulkResult, b: BulkResult): number =>
  a.projectName.localeCompare(b.projectName) ||
  a.person.localeCompare(b.person)

/**
 * Adds or removes people as members of the ticked projects.
 *
 * Opening it reads every project's members, a few at a time (quietly: an
 * unreadable project is reported failed in the summary, not notified). Then
 * the steps are: pick people, a red confirmation for remove, the run with its
 * progress and Stop, and the summary with the failed and not-run calls. The
 * dialog can't be closed while a run is going.
 */
const BulkMembersModal: React.FC<BulkMembersModalProps> = ({
  open,
  action,
  projects,
  onClose,
}) => {
  const getAxios = useAuthedAxios()
  const username = useUserStore((state) => state.user?.username ?? "")
  const unitUsersQuery = useUnitUsersQuery(open)
  const bulk = useBulkMembers()

  const [search, setSearch] = useState<string>("")
  const [ticked, setTicked] = useState<string[]>([])
  const [confirming, setConfirming] = useState<boolean>(false)
  const [stopping, setStopping] = useState<boolean>(false)

  // Read fresh on open: skips are decided from these lists. The reads share a
  // limiter, so a big folder never fires them all at once.
  const membersQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: queryKeys.projects.members(project.id),
      enabled: open,
      refetchOnMount: "always" as const,
      meta: { silent: true },
      queryFn: (): Promise<string[]> =>
        limitMembersRead(async () => {
          const res = await projectMembers(await getAxios(), project.id)
          if (typeof res.data !== "string") {
            throw new Error("Couldn't read its members")
          }
          return res.data
            .split("\n")
            .filter((member) => member.trim() !== "")
        }),
    })),
  })

  const members: MembersByProject = {}
  projects.forEach((project, index) => {
    const query = membersQueries[index]
    if (query?.error) members[project.id] = query.error
    else if (query?.data) members[project.id] = query.data
  })
  const unreadable = projects.filter(
    (project) => members[project.id] instanceof Error
  ).length
  const isReading =
    unitUsersQuery.isPending || membersQueries.some((query) => query.isFetching)

  // Add offers the unit's people bar the user; remove offers anyone who is a
  // member (not as owner) of at least one ticked project.
  const candidates: string[] =
    action === "add"
      ? uniqueNames(unitUsersQuery.data ?? []).filter(
          (person) => sameName(person) !== sameName(username)
        )
      : uniqueNames(
          Object.values(members).flatMap((list) =>
            list instanceof Error ? [] : list
          )
        ).sort((a, b) => a.localeCompare(b))

  const counts = memberCounts(candidates, projects, members)
  const people =
    action === "add"
      ? candidates
      : candidates.filter((person) => counts[person] > 0)
  const shown = people.filter((person) =>
    person.toLowerCase().includes(search.trim().toLowerCase())
  )
  const tickedPeople = people.filter((person) => ticked.includes(person))

  // Start each opening afresh.
  useEffect(() => {
    if (!open) {
      setSearch("")
      setTicked([])
      setConfirming(false)
      setStopping(false)
      bulk.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const running = bulk.status === "running"
  const finished = bulk.status === "done"
  const count = projects.length
  const title =
    action === "add"
      ? `Add members to ${projectsText(count)}`
      : `Remove members from ${projectsText(count)}`

  const problems = bulk.results.filter(isProblem).sort(byProjectThenPerson)
  const canRetry = bulk.results.some(isRetryable)
  const keepIds = finished
    ? [...new Set(problems.map((result) => result.projectId))]
    : []

  const close = (): void => {
    if (running) return
    onClose(finished, keepIds)
  }

  const toggle = (person: string): void =>
    setTicked((current) =>
      current.includes(person)
        ? current.filter((name) => name !== person)
        : [...current, person]
    )

  const run = (): void => {
    setConfirming(false)
    setStopping(false)
    void bulk.start(action, tickedPeople, projects, members)
  }

  const retry = (): void => {
    setStopping(false)
    void bulk.retryFailed()
  }

  const stop = (): void => {
    setStopping(true)
    bulk.stop()
  }

  // Remove confirms first, counting projects that will get a call.
  const confirmText = (): string => {
    const { calls } = planBulkChanges(action, tickedPeople, projects, members)
    const reached = new Set(calls.map((call) => call.projectId)).size
    const who =
      tickedPeople.length === 1 ? tickedPeople[0] : `${tickedPeople.length} people`
    return `Remove ${who} from ${projectsText(reached)}?`
  }

  let body: React.ReactNode
  let footer: React.ReactNode

  if (running) {
    const { done, total } = bulk.progress
    body = (
      <Progress
        percent={total === 0 ? 100 : Math.round((done / total) * 100)}
        format={() => `${done} of ${total}`}
      />
    )
    footer = (
      <Button onClick={stop} disabled={stopping}>
        {stopping ? "Stopping…" : "Stop"}
      </Button>
    )
  } else if (finished) {
    body = (
      <div className="flex flex-col gap-3">
        <div>
          {summariseResults(action, bulk.results).map((line) => (
            <p key={line} className="m-0">
              {line}
            </p>
          ))}
          {keepIds.length > 0 && (
            <p className="m-0">The projects that didn't finish stay ticked.</p>
          )}
        </div>
        {problems.length > 0 && (
          <ul
            aria-label="Failed and not run"
            className="m-0 max-h-64 list-none overflow-y-auto p-0"
          >
            {problems.map((result) => (
              <li
                key={`${result.projectId}\n${result.person}`}
                className="flex gap-2 border-b border-gray-100 py-1"
              >
                <span className="font-medium">{result.projectName}</span>
                <span>{result.person}</span>
                <span className="ml-auto text-gray-500">{reasonOf(result)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
    footer = (
      <>
        {canRetry && <Button onClick={retry}>Retry failed</Button>}
        <Button type="primary" onClick={close}>
          Done
        </Button>
      </>
    )
  } else if (isReading) {
    body = (
      <div className="flex justify-center py-6">
        <Spin tip={`Reading members of ${projectsText(count)}…`}>
          <div className="h-12 w-64" />
        </Spin>
      </div>
    )
    footer = <Button onClick={close}>Cancel</Button>
  } else if (confirming) {
    body = <Alert type="error" showIcon message={confirmText()} />
    footer = (
      <>
        <Button onClick={() => setConfirming(false)}>Cancel</Button>
        <Button type="primary" danger onClick={run}>
          Remove
        </Button>
      </>
    )
  } else {
    // With no one to remove, say when reads failed; the "no members" line
    // only stands when some projects were read.
    body =
      people.length === 0 && action === "remove" ? (
        <div className="flex flex-col gap-1 text-gray-600">
          {unreadable > 0 && (
            <p className="m-0">
              Couldn't read the members of {projectsText(unreadable)}.
            </p>
          )}
          {unreadable < count && (
            <p className="m-0">
              No one to remove: none of the selected projects have members.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Input
            allowClear
            prefix={<FontAwesomeIcon icon={faSearch} />}
            placeholder="Search people"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul
            aria-label="People"
            className="m-0 max-h-80 list-none overflow-y-auto p-0"
          >
            {shown.map((person) => (
              <li key={person} className="py-1">
                <Checkbox
                  checked={ticked.includes(person)}
                  onChange={() => toggle(person)}
                >
                  {person}
                  {counts[person] > 0 && (
                    <span className="ml-2 text-gray-500">
                      member of {counts[person]} of {count}
                    </span>
                  )}
                </Checkbox>
              </li>
            ))}
          </ul>
        </div>
      )
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <Button
          type="primary"
          disabled={tickedPeople.length === 0}
          onClick={action === "add" ? run : () => setConfirming(true)}
        >
          {action === "add" ? "Add" : "Remove"}
        </Button>
      </>
    )
  }

  return (
    <Modal
      open={open}
      centered
      title={title}
      closable={!running}
      maskClosable={!running}
      keyboard={!running}
      onCancel={close}
      footer={footer}
      destroyOnHidden
    >
      {body}
    </Modal>
  )
}

export default BulkMembersModal
